import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import tinycolor from "tinycolor2";
import { useAttributeSumLineChart, resolveDefaultAnchor } from "./hooks/useAttributeSumLineChart";
import { toCumulative } from "./utils/toCumulative";
import {
  validateComparisonMode,
  getComparisonLegendLabel,
  trimFutureBuckets,
} from "../_shared/comparisonPeriod";
import { ZONE_COLORS, sanitizeThresholdFields } from "./utils/thresholdUtils";
import { TABLEAU_10 } from "../../../features/variants/VariantExplorerExtended/utils/colorUtils";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { getBerlinToday, formatDateOnly } from "../../../utils/berlinDate";
import BaseTile from "../../../components/Tile/BaseTile";
import TileContent, {
  TileStatusContent,
} from "../../../components/Tile/TileContent";
import TileSettingsModal from "../../../components/common/TileSettingsModal";
import LocalFiltersActiveBadge from "../../../components/common/LocalFiltersActiveBadge";
import AttributeSumLineChartSettings from "./AttributeSumLineChartSettings";
import * as Tooltip from "@radix-ui/react-tooltip";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend,
);

/**
 * Chart.js plugin that paints threshold zone bands as subtle background shading.
 *
 * Draws filled polygons in beforeDraw so they sit behind all datasets.
 * Each band is defined by lower/upper boundary arrays (per data point) and a fill color.
 * null lower = chart bottom (origin); null upper = chart top.
 * Supports both constant boundaries (normal mode) and dynamic curves (cumulative mode).
 */
const thresholdZonePlugin = {
  id: "thresholdZones",
  beforeDraw(chart) {
    const bands = chart.options.plugins?.thresholdZones?.bands;
    if (!bands?.length) return;

    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !scales.y || !scales.x) return;

    const { left, right, top, bottom } = chartArea;
    const yScale = scales.y;
    const xScale = scales.x;
    const pointCount = bands[0].upper?.length || bands[0].lower?.length || 0;
    if (pointCount === 0) return;

    ctx.save();

    for (const band of bands) {
      ctx.fillStyle = band.color;
      ctx.beginPath();

      // Trace upper boundary left-to-right
      for (let i = 0; i < pointCount; i++) {
        const x = xScale.getPixelForValue(i);
        const y = band.upper
          ? Math.max(top, Math.min(yScale.getPixelForValue(band.upper[i]), bottom))
          : top;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      // Extend upper boundary to right edge of chart area
      const lastUpperY = band.upper
        ? Math.max(top, Math.min(yScale.getPixelForValue(band.upper[pointCount - 1]), bottom))
        : top;
      ctx.lineTo(right, lastUpperY);

      // Trace lower boundary right-to-left
      const lastLowerY = band.lower
        ? Math.max(top, Math.min(yScale.getPixelForValue(band.lower[pointCount - 1]), bottom))
        : bottom;
      ctx.lineTo(right, lastLowerY);
      for (let i = pointCount - 1; i >= 0; i--) {
        const x = xScale.getPixelForValue(i);
        const y = band.lower
          ? Math.max(top, Math.min(yScale.getPixelForValue(band.lower[i]), bottom))
          : bottom;
        ctx.lineTo(x, y);
      }
      // Extend lower boundary to left edge
      const firstLowerY = band.lower
        ? Math.max(top, Math.min(yScale.getPixelForValue(band.lower[0]), bottom))
        : bottom;
      ctx.lineTo(left, firstLowerY);

      // Close to starting point
      const firstUpperY = band.upper
        ? Math.max(top, Math.min(yScale.getPixelForValue(band.upper[0]), bottom))
        : top;
      ctx.lineTo(left, firstUpperY);

      ctx.closePath();
      ctx.fill();
    }

    ctx.restore();
  },
};

ChartJS.register(thresholdZonePlugin);

/**
 * Derives border (full opacity) and background (0.7 opacity) rgba strings
 * from a hex color returned by getColorForActivity.
 */
function chartColorsFromHex(hex) {
  const c = tinycolor(hex);
  return {
    border: c.setAlpha(1).toRgbString(),
    bg: c.setAlpha(0.7).toRgbString(),
  };
}

function fmtValue(n) {
  const num = Number(n || 0);
  const dec = Number.isInteger(num) ? 0 : 2;
  return num.toLocaleString("de-DE", {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  });
}

const GRANULARITY_LABELS = {
  day: "Day",
  week: "Week",
  month: "Month",
  year: "Year",
};

function formatXAxisDateLabel(value, granularity) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  if (granularity === "year") {
    return parsed.toLocaleDateString("de-DE", { month: "short" });
  }
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "short",
  });
}

function formatGermanDateLabel(value) {
  const parsed = new Date(value + "T00:00:00");
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Formats a Date to YYYY-MM-DD using local time (avoids UTC shift from toISOString). */
function fmtLocal(dt) {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function computeDisplayRange(dateStr, gran) {
  if (!dateStr) return { startDate: "", endDate: "" };
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return { startDate: "", endDate: "" };

  if (gran === "day") return { startDate: fmtLocal(d), endDate: fmtLocal(d) };

  if (gran === "week") {
    const day = d.getDay();
    const mon = new Date(d);
    mon.setDate(d.getDate() - ((day + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { startDate: fmtLocal(mon), endDate: fmtLocal(sun) };
  }

  if (gran === "month") {
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { startDate: fmtLocal(start), endDate: fmtLocal(end) };
  }

  return {
    startDate: `${d.getFullYear()}-01-01`,
    endDate: `${d.getFullYear()}-12-31`,
  };
}

function defaultAnchor(gran) {
  const today = new Date();
  if (gran === "day") {
    const y = new Date(today);
    y.setDate(today.getDate() - 1);
    return fmtLocal(y);
  }
  return fmtLocal(today);
}

/** Isolated date input — uncontrolled to prevent Chrome's date-section cursor from
 *  resetting on every React re-render.
 *
 *  Chrome fires native 'change' on each section auto-advance (month→day→year) during
 *  keyboard typing, NOT only when the picker closes. We distinguish the two paths via
 *  a pointer flag: if the last interaction was a pointer event (click = calendar picker),
 *  commit on 'change'; if it was a key event (keyboard typing), defer to Enter. */
const DateInput = React.memo(function DateInput({ value, onChange, disabled }) {
  const committed = useRef(value);
  const inputRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const isPointerRef = useRef(false);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Sync from parent when value changes externally (e.g. granularity button click)
  useEffect(() => {
    if (inputRef.current) inputRef.current.value = value;
    committed.current = value;
  }, [value]);

  const commit = useCallback(() => {
    const val = inputRef.current?.value;
    if (!val || val === committed.current) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(val) || isNaN(new Date(val + "T00:00:00").getTime())) return;
    committed.current = val;
    onChangeRef.current(val);
  }, []);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const onPointerDown = () => { isPointerRef.current = true; };
    const onKeyDown = () => { isPointerRef.current = false; };
    const onNativeChange = () => {
      if (isPointerRef.current) {
        isPointerRef.current = false;
        commit();
      }
    };
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('change', onNativeChange);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('change', onNativeChange);
    };
  }, [commit]);

  return (
    <input
      ref={inputRef}
      type="date"
      defaultValue={value}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); }}
      onBlur={commit}
      disabled={disabled}
      className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
    />
  );
});

function AttributeSumLineChart({
  activeFile,
  onRefresh,
  onGetHeaderActions,
  onTitleChange,
  onRegisterSettingsGetter,
  initialSettings,
}) {
  const {
    loading,
    error,
    seriesData,
    datasetLastDate,
    objectTypes,
    eventTypes,
    objectTypeNumericAttributes,
    eventTypeNumericAttributes,
    isMetadataLoaded,
    // TileConfig
    title,
    attributeSource,
    selectedObjectTypes,
    selectedObjectAttributes,
    selectedEventTypes,
    selectedEventAttributes,
    enabledGranularities,
    displayMode,
    valueAggregation,
    timestampAnchor,
    anchorActivity,
    activityOptions,
    comparisonEnabled,
    comparisonMode,
    limitComparisonToCurrentProgress,
    comparisonSeriesData,
    mainLineColor,
    // Threshold config
    thresholdType,
    thresholdTarget,
    thresholdWarningPercentage,
    thresholdTargetPerDay,
    thresholdLowerThreshold,
    thresholdUpperThreshold,
    thresholdLowerThresholdPerDay,
    thresholdUpperThresholdPerDay,
    thresholdData,
    // Reference line
    referenceLine,
    referenceLineData,
    // ViewState
    granularity,
    selectedDate,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
    dateRangePresetKey,
    // TileConfig setters
    setTitle,
    setAttributeSource,
    setSelectedObjectTypes,
    setSelectedObjectAttributes,
    setSelectedEventTypes,
    setSelectedEventAttributes,
    setEnabledGranularities,
    setDisplayMode,
    setValueAggregation,
    setTimestampAnchor,
    setAnchorActivity,
    setComparisonEnabled,
    setComparisonMode,
    setLimitComparisonToCurrentProgress,
    setMainLineColor,
    // Threshold config setters
    setThresholdType,
    setThresholdTarget,
    setThresholdWarningPercentage,
    setThresholdTargetPerDay,
    setThresholdLowerThreshold,
    setThresholdUpperThreshold,
    setThresholdLowerThresholdPerDay,
    setThresholdUpperThresholdPerDay,
    // Reference line setter
    setReferenceLine,
    // ViewState setters
    setGranularity,
    setSelectedDate,
    setViewMode,
    setDateRangeStart,
    setDateRangeEnd,
    setDateRangePresetKey,
    // Local filters
    localFilters,
    updateLocalFilter,
    clearLocalFilters,
    hasLocalFilters,
    localFilterCount,
    currentFilters,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters,
    // Actions
    fetchMetadata,
    fetchChartData,
    fetchNumericAttributesForObjectTypes,
  } = useAttributeSumLineChart(activeFile, initialSettings);

  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({
      title, attributeSource, selectedObjectTypes, selectedObjectAttributes,
      selectedEventTypes, selectedEventAttributes, enabledGranularities,
      displayMode, valueAggregation, timestampAnchor, anchorActivity, comparisonEnabled, comparisonMode,
      limitComparisonToCurrentProgress,
      thresholdType, thresholdTarget, thresholdWarningPercentage, thresholdTargetPerDay,
      thresholdLowerThreshold, thresholdUpperThreshold,
      thresholdLowerThresholdPerDay, thresholdUpperThresholdPerDay,
      granularity, selectedDate, viewMode, dateRangeStart, dateRangeEnd, dateRangePresetKey,
      localFilters,
      referenceLine,
      mainLineColor,
    }));
  }, [title, attributeSource, selectedObjectTypes, selectedObjectAttributes, // eslint-disable-line react-hooks/exhaustive-deps
      selectedEventTypes, selectedEventAttributes, enabledGranularities,
      displayMode, valueAggregation, timestampAnchor, anchorActivity, comparisonEnabled, comparisonMode,
      limitComparisonToCurrentProgress,
      thresholdType, thresholdTarget, thresholdWarningPercentage, thresholdTargetPerDay,
      thresholdLowerThreshold, thresholdUpperThreshold,
      thresholdLowerThresholdPerDay, thresholdUpperThresholdPerDay,
      granularity, selectedDate, viewMode, dateRangeStart, dateRangeEnd, dateRangePresetKey, localFilters, referenceLine, mainLineColor]);

  // Re-sync the tile title to the parent header — also fires after the
  // mount-restore effect applies a saved title.
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => {
    onTitleChangeRef.current?.(title || "Attribute Sum Line Chart");
  }, [title]);

  const isFirstFilterRenderRef = useRef(true);

  useEffect(() => {
    if (isFirstFilterRenderRef.current) {
      isFirstFilterRenderRef.current = false;
      return;
    }
    fetchMetadata();
    if (hasConfig) {
      fetchChartData();
    }
  }, [currentFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const { t } = useTranslation();
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [draftSettings, setDraftSettings] = useState(null);
  const [viewDateError, setViewDateError] = useState("");
  // pendingApply triggers a fetch via useEffect AFTER state updates land,
  // so fetchChartData always runs with the latest settings/local filters.
  const [pendingApply, setPendingApply] = useState(false);

  const tileConfigRef = useRef({
    title,
    attributeSource,
    selectedObjectTypes,
    selectedObjectAttributes,
    selectedEventTypes,
    selectedEventAttributes,
    enabledGranularities,
    displayMode,
    valueAggregation,
    timestampAnchor,
    anchorActivity,
    comparisonEnabled,
    comparisonMode,
    limitComparisonToCurrentProgress,
    thresholdType,
    thresholdTarget,
    thresholdWarningPercentage,
    thresholdTargetPerDay,
    thresholdLowerThreshold,
    thresholdUpperThreshold,
    thresholdLowerThresholdPerDay,
    thresholdUpperThresholdPerDay,
    referenceLine,
    mainLineColor,
  });
  useEffect(() => {
    tileConfigRef.current = {
      title,
      attributeSource,
      selectedObjectTypes,
      selectedObjectAttributes,
      selectedEventTypes,
      selectedEventAttributes,
      enabledGranularities,
      displayMode,
      valueAggregation,
      timestampAnchor,
      anchorActivity,
      comparisonEnabled,
      comparisonMode,
      limitComparisonToCurrentProgress,
      thresholdType,
      thresholdTarget,
      thresholdWarningPercentage,
      thresholdTargetPerDay,
      thresholdLowerThreshold,
      thresholdUpperThreshold,
      thresholdLowerThresholdPerDay,
      thresholdUpperThresholdPerDay,
      referenceLine,
      mainLineColor,
    };
  }, [
    title,
    attributeSource,
    selectedObjectTypes,
    selectedObjectAttributes,
    selectedEventTypes,
    selectedEventAttributes,
    enabledGranularities,
    displayMode,
    valueAggregation,
    timestampAnchor,
    anchorActivity,
    comparisonEnabled,
    comparisonMode,
    limitComparisonToCurrentProgress,
    thresholdType,
    thresholdTarget,
    thresholdWarningPercentage,
    thresholdTargetPerDay,
    thresholdLowerThreshold,
    thresholdUpperThreshold,
    thresholdLowerThresholdPerDay,
    thresholdUpperThresholdPerDay,
    referenceLine,
    mainLineColor,
  ]);

  const displayRange = useMemo(
    () => computeDisplayRange(selectedDate, granularity),
    [selectedDate, granularity],
  );

  // Check if configured
  const hasConfig = useMemo(() => {
    if (attributeSource === "object") {
      return (
        selectedObjectTypes.length > 0 && selectedObjectAttributes.length > 0
      );
    }
    return selectedEventTypes.length > 0 && selectedEventAttributes.length > 0;
  }, [
    attributeSource,
    selectedObjectTypes,
    selectedObjectAttributes,
    selectedEventTypes,
    selectedEventAttributes,
  ]);

  // Settings modal handlers

  const handleOpenSettings = useCallback(() => {
    startDraftMode();
    const cfg = tileConfigRef.current;
    setDraftSettings({
      title: cfg.title,
      attributeSource: cfg.attributeSource,
      selectedObjectTypes: [...cfg.selectedObjectTypes],
      selectedObjectAttributes: [...cfg.selectedObjectAttributes],
      selectedEventTypes: [...cfg.selectedEventTypes],
      selectedEventAttributes: [...cfg.selectedEventAttributes],
      enabledGranularities: [...cfg.enabledGranularities],
      displayMode: cfg.displayMode,
      valueAggregation: cfg.valueAggregation,
      timestampAnchor: cfg.timestampAnchor,
      anchorActivity: cfg.anchorActivity,
      comparisonEnabled: cfg.comparisonEnabled,
      comparisonMode: cfg.comparisonMode,
      limitComparisonToCurrentProgress: cfg.limitComparisonToCurrentProgress,
      thresholdType: cfg.thresholdType,
      thresholdTarget: cfg.thresholdTarget,
      thresholdWarningPercentage: cfg.thresholdWarningPercentage,
      thresholdTargetPerDay: cfg.thresholdTargetPerDay,
      thresholdLowerThreshold: cfg.thresholdLowerThreshold,
      thresholdUpperThreshold: cfg.thresholdUpperThreshold,
      thresholdLowerThresholdPerDay: cfg.thresholdLowerThresholdPerDay,
      thresholdUpperThresholdPerDay: cfg.thresholdUpperThresholdPerDay,
      referenceLine: { ...cfg.referenceLine },
      mainLineColor: cfg.mainLineColor,
    });
    setIsSettingsModalOpen(true);
  }, [startDraftMode]);

  const handleCloseSettings = useCallback(() => {
    setIsSettingsModalOpen(false);
    setDraftSettings(null);
    cancelDraftFilters();
  }, [cancelDraftFilters]);

  const handleApplySettings = useCallback(() => {
    if (!draftSettings) return;

    const normalizedGranularity = draftSettings.enabledGranularities.includes(
      granularity,
    )
      ? granularity
      : draftSettings.enabledGranularities[0] || "day";

    const validatedCompMode = validateComparisonMode(
      draftSettings.comparisonMode,
    );

    setIsSettingsModalOpen(false);
    setDraftSettings(null);

    if (onTitleChange) onTitleChange(draftSettings.title);

    setTitle(draftSettings.title);
    setAttributeSource(draftSettings.attributeSource);
    setSelectedObjectTypes(draftSettings.selectedObjectTypes);
    setSelectedObjectAttributes(draftSettings.selectedObjectAttributes);
    setSelectedEventTypes(draftSettings.selectedEventTypes);
    setSelectedEventAttributes(draftSettings.selectedEventAttributes);
    setEnabledGranularities(draftSettings.enabledGranularities);
    setDisplayMode(draftSettings.displayMode);
    setValueAggregation(draftSettings.valueAggregation);
    setTimestampAnchor(draftSettings.timestampAnchor);
    setAnchorActivity(draftSettings.anchorActivity);
    setComparisonEnabled(draftSettings.comparisonEnabled);
    setComparisonMode(validatedCompMode);
    setLimitComparisonToCurrentProgress(draftSettings.limitComparisonToCurrentProgress);
    setThresholdType(draftSettings.thresholdType);
    setThresholdTarget(draftSettings.thresholdTarget);
    setThresholdWarningPercentage(draftSettings.thresholdWarningPercentage);
    setThresholdTargetPerDay(draftSettings.thresholdTargetPerDay);
    setThresholdLowerThreshold(draftSettings.thresholdLowerThreshold);
    setThresholdUpperThreshold(draftSettings.thresholdUpperThreshold);
    setThresholdLowerThresholdPerDay(draftSettings.thresholdLowerThresholdPerDay);
    setThresholdUpperThresholdPerDay(draftSettings.thresholdUpperThresholdPerDay);
    setReferenceLine(draftSettings.referenceLine);
    setMainLineColor(draftSettings.mainLineColor);
    setGranularity(normalizedGranularity);

    applyDraftFilters();
    setPendingApply(true);
  }, [
    draftSettings,
    granularity,
    applyDraftFilters,
    onTitleChange,
    setTitle,
    setAttributeSource,
    setSelectedObjectTypes,
    setSelectedObjectAttributes,
    setSelectedEventTypes,
    setSelectedEventAttributes,
    setEnabledGranularities,
    setDisplayMode,
    setValueAggregation,
    setTimestampAnchor,
    setAnchorActivity,
    setComparisonEnabled,
    setComparisonMode,
    setLimitComparisonToCurrentProgress,
    setThresholdType,
    setThresholdTarget,
    setThresholdWarningPercentage,
    setThresholdTargetPerDay,
    setThresholdLowerThreshold,
    setThresholdUpperThreshold,
    setThresholdLowerThresholdPerDay,
    setThresholdUpperThresholdPerDay,
    setReferenceLine,
    setMainLineColor,
    setGranularity,
  ]);

  useEffect(() => {
    if (!pendingApply) return;
    setPendingApply(false);
    fetchChartData();
  }, [pendingApply, fetchChartData]);

  // Auto-load & refresh

  useAutoLoad(fetchMetadata, [activeFile], isMetadataLoaded);

  const fetchChartDataRef = useRef(fetchChartData);
  useEffect(() => {
    fetchChartDataRef.current = fetchChartData;
  }, [fetchChartData]);

  const hasConfigRef = useRef(hasConfig);
  useEffect(() => {
    hasConfigRef.current = hasConfig;
  }, [hasConfig]);

  const chartRef = useRef(null);
  const chartWrapperRef = useRef(null);
  useEffect(() => {
    const el = chartWrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => { chartRef.current?.resize(); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (onRefresh) {
      onRefresh(async () => {
        if (isMetadataLoaded && hasConfigRef.current)
          await fetchChartDataRef.current();
      });
    }
  }, [onRefresh, isMetadataLoaded]);

  // Header

  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <LocalFiltersActiveBadge
          hasLocalFilters={hasLocalFilters}
          localFilterCount={localFilterCount}
          onClearLocalFilters={clearLocalFilters}
        />
        <button
          onClick={handleOpenSettings}
          className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-label={t('common:actions.openSettings')}
        >
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <span className="text-xs">{t('common:actions.settings')}</span>
        </button>
      </div>
    ),
    [handleOpenSettings, hasLocalFilters, localFilterCount, clearLocalFilters],
  );

  useEffect(() => {
    if (onGetHeaderActions) onGetHeaderActions(headerActions);
  }, [onGetHeaderActions, headerActions]);

  // View Mode handlers

  const triggerAutoApply = useCallback(
    (overrides = {}) => {
      const effectiveViewMode = overrides.viewMode ?? viewMode;
      const effectiveDateRangeStart =
        overrides.dateRangeStart ?? dateRangeStart;
      const effectiveDateRangeEnd = overrides.dateRangeEnd ?? dateRangeEnd;

      if (effectiveViewMode === "daterange") {
        if (!effectiveDateRangeStart || !effectiveDateRangeEnd) {
          setViewDateError("Select start and end dates.");
          return false;
        }
        if (effectiveDateRangeStart > effectiveDateRangeEnd) {
          setViewDateError("Start date must be on or before end date.");
          return false;
        }
      }
      setViewDateError("");

      const mergedOverrides = {
        viewMode: effectiveViewMode,
        dateRangeStart: effectiveDateRangeStart,
        dateRangeEnd: effectiveDateRangeEnd,
        ...overrides,
      };

      fetchChartData(mergedOverrides);
      return true;
    },
    [fetchChartData, viewMode, dateRangeStart, dateRangeEnd],
  );

  const handleGranularityChange = useCallback(
    (g) => {
      setGranularity(g);
      const baseAnchor = selectedDate || defaultAnchor(g);
      const normalizedAnchor =
        datasetLastDate && baseAnchor > datasetLastDate
          ? datasetLastDate
          : baseAnchor;
      setSelectedDate(normalizedAnchor);

      const validatedMode = validateComparisonMode(comparisonMode);
      if (validatedMode !== comparisonMode) {
        setComparisonMode(validatedMode);
      }

      triggerAutoApply({
        granularity: g,
        selectedDate: normalizedAnchor,
        comparisonMode: validatedMode,
      });
    },
    [
      setGranularity,
      setSelectedDate,
      setComparisonMode,
      selectedDate,
      datasetLastDate,
      comparisonMode,
      triggerAutoApply,
    ],
  );

  const handleViewModeChange = useCallback(
    (nextMode) => {
      if (nextMode === viewMode) return;
      if (nextMode === "daterange") {
        // Get period from any of the series responses
        const firstSeries = seriesData ? Object.values(seriesData)[0] : null;
        if (firstSeries?.period_start && firstSeries?.period_end) {
          setDateRangeStart(firstSeries.period_start);
          setDateRangeEnd(firstSeries.period_end);
        }
      } else {
        const baseAnchor =
          dateRangeStart || selectedDate || defaultAnchor(granularity);
        const normalizedAnchor =
          datasetLastDate && baseAnchor > datasetLastDate
            ? datasetLastDate
            : baseAnchor;
        setSelectedDate(normalizedAnchor);
      }
      setViewMode(nextMode);
    },
    [
      viewMode,
      selectedDate,
      seriesData,
      dateRangeStart,
      datasetLastDate,
      setDateRangeStart,
      setDateRangeEnd,
      setSelectedDate,
      setViewMode,
    ],
  );

  const handleSelectedDateCommit = useCallback(
    (nextDate) => {
      setSelectedDate(nextDate);
      if (viewMode === "granularity") {
        triggerAutoApply({ viewMode: "granularity", selectedDate: nextDate });
      }
    },
    [setSelectedDate, viewMode, triggerAutoApply],
  );

  const handleDateRangeStartCommit = useCallback(
    (nextDate) => {
      setDateRangeStart(nextDate);
      setViewDateError("");
    },
    [setDateRangeStart],
  );

  const handleDateRangeEndCommit = useCallback(
    (nextDate) => {
      setDateRangeEnd(nextDate);
      setViewDateError("");
    },
    [setDateRangeEnd],
  );

  const handleApplyDateRange = useCallback(() => {
    triggerAutoApply({ viewMode: "daterange" });
  }, [triggerAutoApply]);

  const handleDateRangePresetChange = useCallback(
    (value) => {
      const start = (value?.startDate || "").slice(0, 10);
      const end = (value?.endDate || "").slice(0, 10);
      const presetKey = value?.presetKey || null;

      setDateRangeStart(start);
      setDateRangeEnd(end);
      setDateRangePresetKey(presetKey);
      setViewDateError("");

      // Also refresh the Granularity-mode anchor right away, computed synchronously
      // here (not left to the hook's own effect) since that effect won't have run yet
      // by the time triggerAutoApply below reads component state — same reason start/
      // end are passed as explicit overrides rather than relying on the just-called
      // setDateRangeStart/setDateRangeEnd having landed.
      const anchor = resolveDefaultAnchor({
        dateRangePresetKey: presetKey,
        localFilterDateRange: localFilters?.dateRange,
        granularity,
        datasetLastDate,
      });
      setSelectedDate(anchor);

      // Deliberately NOT forcing viewMode — stays in whichever mode (Granularity or
      // Date Range) the user currently has active; triggerAutoApply reads the current
      // viewMode and refetches using whichever of these two just-resolved values
      // applies to it.
      triggerAutoApply({ dateRangeStart: start, dateRangeEnd: end, selectedDate: anchor });
    },
    [
      setDateRangeStart,
      setDateRangeEnd,
      setDateRangePresetKey,
      setSelectedDate,
      localFilters?.dateRange,
      granularity,
      datasetLastDate,
      triggerAutoApply,
    ],
  );

  // Chart data

  // Build per-attribute item maps from seriesData
  const { labels, attrItemsMap, firstResponse } = useMemo(() => {
    if (!seriesData || Object.keys(seriesData).length === 0)
      return { labels: [], attrItemsMap: {}, firstResponse: null };

    const entries = Object.entries(seriesData);
    const first = entries[0][1];

    // Use labels from the first series (all should share the same time buckets)
    const labelGranularity = viewMode === "daterange" ? "day" : granularity;
    const firstItems = first.chart_items || [];

    // "Limit previous periods to current progress" trims the CURRENT period's
    // own trailing buckets that haven't happened yet, not just the comparison
    // series — so the future doesn't render at all. Truncate by INDEX (not a
    // per-array filter) so every attribute's array and the label list stay the
    // same length and positionally aligned; the comparison-series zip below is
    // bound by `labels.length`, so trimming it here trims that too. Buckets
    // are chronologically sorted, so "count of leading buckets <= today" is a
    // valid prefix length. Day granularity is excluded (no partial-period
    // concept, mirrors clampRangeEndToToday).
    let cutoffCount = firstItems.length;
    if (limitComparisonToCurrentProgress) {
      const todayISO = formatDateOnly(getBerlinToday());
      cutoffCount = trimFutureBuckets(firstItems, labelGranularity, todayISO).length;
    }

    const bucketLabels = firstItems.slice(0, cutoffCount).map((d) =>
      formatXAxisDateLabel(d.bucket_label, labelGranularity),
    );

    // Build raw items map
    const rawMap = {};
    for (const [attr, resp] of entries) {
      rawMap[attr] = (resp.chart_items || []).slice(0, cutoffCount);
    }

    // Apply cumulative transform if needed
    const itemsMap =
      displayMode === "cumulative" ? toCumulative(rawMap) : rawMap;

    return {
      labels: bucketLabels,
      attrItemsMap: itemsMap,
      firstResponse: first,
    };
  }, [seriesData, granularity, viewMode, displayMode, limitComparisonToCurrentProgress]);

  const hasData = labels.length > 0;
  const attrNames = Object.keys(attrItemsMap);

  /**
   * Processes comparison series data: extracts chart items, applies cumulative
   * transform if needed, and aligns to the primary labels length.
   */
  const comparisonAttrItemsMap = useMemo(() => {
    if (!comparisonEnabled || !comparisonSeriesData) return null;

    const rawMap = {};
    for (const [attr, resp] of Object.entries(comparisonSeriesData)) {
      rawMap[attr] = resp.chart_items || [];
    }

    const itemsMap = displayMode === "cumulative" ? toCumulative(rawMap) : rawMap;

    // Check if any series has data
    const hasAnyData = Object.values(itemsMap).some((items) => items.length > 0);
    return hasAnyData ? itemsMap : null;
  }, [comparisonEnabled, comparisonSeriesData, displayMode]);

  const chartjsData = useMemo(() => {
    if (!hasData) return null;

    // Buckets for calendar days after today (Europe/Berlin) haven't happened yet —
    // the backend still returns them (zero-filled) to complete the requested period,
    // but they must not be drawn as real data points/line.
    const todayStr = formatDateOnly(getBerlinToday());

    const datasets = attrNames.map((attr, idx) => {
      const seriesHex = idx === 0 ? (mainLineColor || "#00a2ed") : TABLEAU_10[idx % TABLEAU_10.length];
      const color = chartColorsFromHex(seriesHex);
      const items = attrItemsMap[attr] || [];
      const dataValues = items.map((d) =>
        d.bucket_start && d.bucket_start > todayStr ? null : d.total_value,
      );

      // Apply point coloring from backend-computed threshold zones
      const attrZones = thresholdData?.zones?.[attr];
      const pointColors = attrZones
        ? attrZones.map((zone) => ZONE_COLORS[zone] || color.border)
        : undefined;

      return {
        label: attr,
        data: dataValues,
        borderColor: color.border,
        backgroundColor: color.bg,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0.1,
        fill: false,
        spanGaps: false,
        ...(pointColors
          ? {
              pointBackgroundColor: pointColors,
              pointBorderColor: pointColors,
            }
          : {}),
      };
    });

    // Add comparison datasets if comparison is enabled and data is available
    if (comparisonAttrItemsMap) {
      const compLabel = getComparisonLegendLabel(comparisonMode, t);
      const compFirst = comparisonSeriesData ? Object.values(comparisonSeriesData)[0] : null;
      const compPeriodRange =
        compFirst?.period_start && compFirst?.period_end
          ? { start: compFirst.period_start, end: compFirst.period_end }
          : null;
      attrNames.forEach((attr, idx) => {
        const compItems = comparisonAttrItemsMap[attr];
        if (!compItems || compItems.length === 0) return;

        const baseHex = idx === 0 ? (mainLineColor || "#00a2ed") : TABLEAU_10[idx % TABLEAU_10.length];
        const compColor = tinycolor(baseHex).setAlpha(1).toRgbString();

        // Align comparison data to primary label count: trim or pad with null
        const alignedData = labels.map((_, i) =>
          i < compItems.length ? compItems[i].total_value : null,
        );

        datasets.push({
          label: attrNames.length > 1 ? `${attr} (${compLabel})` : compLabel,
          data: alignedData,
          borderColor: compColor,
          backgroundColor: compColor,
          borderWidth: 1.5,
          pointRadius: 2,
          pointHoverRadius: 4,
          pointStyle: "triangle",
          tension: 0.1,
          fill: false,
          borderDash: [6, 3],
          spanGaps: false,
          periodRange: compPeriodRange,
        });
      });
    }

    // Add threshold reference line datasets from backend-computed data
    if (thresholdData?.reference_lines?.length > 0) {
      for (const refLine of thresholdData.reference_lines) {
        datasets.push({
          label: refLine.label,
          data: refLine.data,
          borderColor: refLine.color,
          backgroundColor: "transparent",
          borderWidth: 1.5,
          pointRadius: 0,
          pointHoverRadius: 0,
          tension: 0,
          fill: false,
          borderDash: refLine.border_dash,
        });
      }
    }

    // Add the Average/Median reference line from its own, independent data basis.
    // Aggregated mode: flat line at the scalar value. Cumulative mode: the backend
    // already returns null entries before the first working day (when non-working
    // days are excluded) — spanGaps: false renders those as a genuine gap, not an
    // interpolated line.
    if (referenceLineData) {
      const refLabel =
        referenceLineData.type === "median" ? "Reference (Median)" : "Reference (Average)";
      const refPeriodRange =
        referenceLine?.periodStart && referenceLine?.periodEnd
          ? { start: referenceLine.periodStart, end: referenceLine.periodEnd }
          : null;
      datasets.push({
        label: refLabel,
        data:
          displayMode === "cumulative"
            ? referenceLineData.data
            : new Array(labels.length).fill(referenceLineData.value),
        borderColor: "#0284fe",
        backgroundColor: "transparent",
        borderWidth: 1.5,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        fill: false,
        borderDash: [5, 3],
        spanGaps: false,
        periodRange: refPeriodRange,
      });
    }

    return { labels, datasets };
  }, [
    hasData,
    labels,
    attrNames,
    attrItemsMap,
    comparisonAttrItemsMap,
    comparisonSeriesData,
    comparisonMode,
    thresholdData,
    referenceLineData,
    referenceLine?.periodStart,
    referenceLine?.periodEnd,
    displayMode,
    mainLineColor,
  ]);

  const chartjsOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          // Always hidden — the unified custom legend row below always renders instead,
          // so the pill/badge styling stays consistent regardless of threshold/reference
          // line/comparison configuration (see dashedBadges/solidBadges).
          display: false,
          position: "top",
          labels: {
            color: "#6b7280",
            font: { size: 11 },
            padding: 12,
            usePointStyle: false,
            boxWidth: 28,
            boxHeight: 2,
          },
        },
        tooltip: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: (context) => {
              const attrName = context.dataset.label;
              const value = context.parsed.y;
              return `${attrName}: ${fmtValue(value)}`;
            },
          },
          // Exclude threshold reference lines from tooltip
          filter: (item) => {
            const label = item.dataset.label || "";
            return (
              label !== "Target" &&
              label !== "Warning" &&
              label !== "Lower Bound" &&
              label !== "Upper Bound"
            );
          },
        },
        // Zone shading plugin data — drawn as subtle background bands by thresholdZonePlugin
        thresholdZones: {
          bands: thresholdData?.zone_bands || [],
        },
      },
      scales: {
        x: {
          offset: false,
          title: {
            display: true,
            text: t('common:chart.date'),
            color: "#4b5563",
            font: { size: 12 },
          },
          ticks: { maxRotation: 45, color: "#6b7280", font: { size: 11 } },
        },
        y: {
          title: {
            display: true,
            text: (() => {
              const aggLabel = valueAggregation === "average" ? "Average"
                : valueAggregation === "median" ? "Median"
                : "Sum";
              return displayMode === "cumulative" ? `Cumulative ${aggLabel}` : aggLabel;
            })(),
            color: "#4b5563",
            font: { size: 12 },
          },
          ticks: { color: "#6b7280", font: { size: 11 } },
          beginAtZero: true,
        },
      },
      interaction: {
        mode: "index",
        intersect: false,
      },
    }),
    [displayMode, valueAggregation, thresholdData],
  );

  // Dashed-line legend badges (Previous Period / Reference Average|Median / threshold
  // Target|Warning|Bound) — hoisted out of the legend JSX so both the threshold-zone
  // layout and the plain single-row layout below can reuse the same badges without
  // duplicating this markup.
  const dashedBadges = useMemo(() => {
    if (!chartjsData) return [];
    return chartjsData.datasets
      .filter((ds) => ds.borderDash)
      .map((ds, i) => {
        const badge = (
          <span className="inline-flex items-center gap-x-1.5 rounded bg-gray-100 px-2 py-0.5">
            <span
              className="inline-block shrink-0"
              style={{
                width: 16,
                height: 2,
                backgroundImage: `repeating-linear-gradient(90deg, ${ds.borderColor} 0 ${ds.borderDash[0]}px, transparent ${ds.borderDash[0]}px ${ds.borderDash[0] + ds.borderDash[1]}px)`,
              }}
            />
            {ds.label}
          </span>
        );
        // Only "Previous Period"/"Reference (Average|Median)" carry a periodRange
        // (set where these datasets are built above) — show the underlying date
        // range on hover so it's clear which window each dashed line actually covers.
        if (!ds.periodRange) {
          return <React.Fragment key={i}>{badge}</React.Fragment>;
        }
        return (
          <Tooltip.Provider key={i} delayDuration={200}>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>{badge}</Tooltip.Trigger>
              <Tooltip.Portal>
                <Tooltip.Content
                  side="top"
                  sideOffset={6}
                  className="bg-white border border-gray-200 rounded-md shadow-lg z-50 px-2.5 py-1.5"
                >
                  <span className="text-xs text-gray-600 whitespace-nowrap">
                    {formatGermanDateLabel(ds.periodRange.start)} – {formatGermanDateLabel(ds.periodRange.end)}
                  </span>
                  <Tooltip.Arrow className="fill-white" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
        );
      });
  }, [chartjsData]);

  const solidBadges = useMemo(() => {
    if (!chartjsData) return [];
    return chartjsData.datasets
      .filter((ds) => !ds.borderDash)
      .map((ds, i) => (
        <span key={i} className="inline-flex items-center gap-x-1.5 rounded bg-gray-100 px-2 py-0.5 pointer-events-auto">
          <span
            className="inline-block shrink-0"
            style={{ width: 16, height: 2, backgroundColor: ds.borderColor }}
          />
          {ds.label}
        </span>
      ));
  }, [chartjsData]);

  // Derived state

  const canCalculate = useMemo(() => {
    if (!isMetadataLoaded) return false;
    if (!selectedDate && !draftSettings) return false;
    const draft = draftSettings || {
      attributeSource,
      selectedObjectTypes,
      selectedObjectAttributes,
      selectedEventTypes,
      selectedEventAttributes,
      enabledGranularities,
    };

    if (!draft.enabledGranularities?.length) return false;
    if (draft.attributeSource === "object") {
      return (
        (draft.selectedObjectTypes?.length ?? 0) > 0 &&
        (draft.selectedObjectAttributes?.length ?? 0) > 0
      );
    }
    return (
      (draft.selectedEventTypes?.length ?? 0) > 0 &&
      (draft.selectedEventAttributes?.length ?? 0) > 0
    );
  }, [
    isMetadataLoaded,
    selectedDate,
    draftSettings,
    attributeSource,
    selectedObjectTypes,
    selectedObjectAttributes,
    selectedEventTypes,
    selectedEventAttributes,
    enabledGranularities,
  ]);

  const showLoadDataPrompt = !isMetadataLoaded && !loading && !error;
  const showConfigPrompt = isMetadataLoaded && !loading && !error && !hasConfig;
  const showContent = isMetadataLoaded && !error && hasConfig;
  const showChart = showContent && hasData;
  const showNoData = showContent && !loading && seriesData !== null && !hasData;
  const showSelectionPrompt = showContent && !loading && seriesData === null;

  useEffect(() => {
    if (!showContent || loading || error || seriesData !== null) return;
    if (viewMode !== "granularity") return;
    triggerAutoApply();
  }, [showContent, loading, error, seriesData, viewMode, triggerAutoApply]);

  // One-shot: load Date Range mode's saved config on first paint only, so a tile
  // restored from a dashboard config renders immediately instead of sitting on
  // "Select Filters to Load Chart" until someone clicks Apply. Deliberately
  // separate from the granularity effect above (which keeps re-firing on every
  // reset) — this ref can only ever fire once per mount, so every later reset
  // (file switch, refresh, settings save) still requires a manual Apply in Date
  // Range mode exactly as before.
  const hasDoneInitialDateRangeLoadRef = useRef(false);
  useEffect(() => {
    if (hasDoneInitialDateRangeLoadRef.current) return;
    if (!showContent || loading || error || seriesData !== null) return;
    hasDoneInitialDateRangeLoadRef.current = true;
    if (viewMode === "daterange" && dateRangeStart && dateRangeEnd) {
      triggerAutoApply();
    }
  }, [showContent, loading, error, seriesData, viewMode, dateRangeStart, dateRangeEnd, triggerAutoApply]);

  // While the modal is open, `draftSettings` is the single source of truth for every
  // field — including legitimate null values (cleared inputs, "None" threshold type).
  // Falling back per-field with `??` would treat null as "missing" and resurrect
  // committed values, making nullable inputs unclearable.
  const settingsValues = draftSettings || {
    title,
    attributeSource,
    selectedObjectTypes,
    selectedObjectAttributes,
    selectedEventTypes,
    selectedEventAttributes,
    enabledGranularities,
    displayMode,
    valueAggregation,
    timestampAnchor,
    anchorActivity,
    comparisonEnabled,
    comparisonMode,
    limitComparisonToCurrentProgress,
    thresholdType,
    thresholdTarget,
    thresholdWarningPercentage,
    thresholdTargetPerDay,
    thresholdLowerThreshold,
    thresholdUpperThreshold,
    thresholdLowerThresholdPerDay,
    thresholdUpperThresholdPerDay,
    referenceLine,
    mainLineColor,
  };

  return (
    <BaseTile title={title} loading={loading} minHeight="lg" padding="sm" className="h-full">
      <TileContent layout="flex" direction="column" gap="sm" grow>
        {showLoadDataPrompt && (
          <TileStatusContent
            status="info"
            title="Load Metadata"
            message="Load metadata first to configure attribute types and selections."
            action={
              <button
                onClick={fetchMetadata}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Load Metadata
              </button>
            }
          />
        )}

        {showConfigPrompt && (
          <TileStatusContent
            status="empty"
            title="Configure Chart"
            message="Open settings to configure attribute source, types, and attributes."
            action={
              <button
                onClick={handleOpenSettings}
                className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
              >
                Configure Chart
              </button>
            }
          />
        )}

        {showContent && (
          <>
            {/* View Mode Controls */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                {/* Time Period label + tooltip */}
                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Time Period</span>
                <Tooltip.Provider delayDuration={200}>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <button type="button" className="inline-flex text-gray-400 hover:text-gray-500 transition-colors" aria-label="Time period help">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Content side="top" align="start" sideOffset={6} className="max-w-[220px] bg-white border border-gray-200 rounded-md shadow-lg z-50">
                        <div className="px-2.5 py-1.5 border-b border-gray-100 bg-primary-50 rounded-t-md">
                          <span className="text-xs font-medium text-primary-700">
                            {viewMode === "granularity" ? "Granularity Mode" : "Date Range Mode"}
                          </span>
                        </div>
                        <div className="px-2.5 py-1.5 text-xs leading-normal text-gray-500">
                          {viewMode === "granularity" ? (
                            <>Pick a granularity (<span className="text-primary-600 font-medium">Day, Week, Month, Year</span>) and select a date. Results are calculated for the full calendar period.</>
                          ) : (
                            <>Select an explicit <span className="text-primary-600 font-medium">start</span> and <span className="text-primary-600 font-medium">end date</span>. Click Apply to update the chart.</>
                          )}
                        </div>
                        <Tooltip.Arrow className="fill-white" />
                      </Tooltip.Content>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>

                {/* Mode-specific controls */}
                {viewMode === "granularity" ? (
                  <>
                    {enabledGranularities.map((g) => {
                      const active = granularity === g;
                      return (
                        <button
                          key={g}
                          type="button"
                          onClick={() => handleGranularityChange(g)}
                          className={`px-2.5 py-1 rounded-md border text-xs font-medium ${
                            active
                              ? "bg-primary-600 text-white border-primary-600"
                              : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                          }`}
                        >
                          {GRANULARITY_LABELS[g] || g}
                        </button>
                      );
                    })}
                    <DateInput value={selectedDate} onChange={handleSelectedDateCommit} disabled={loading} />
                  </>
                ) : (
                  <>
                    <DateInput value={dateRangeStart} onChange={handleDateRangeStartCommit} disabled={loading} />
                    <span className="text-xs text-gray-500">to</span>
                    <DateInput value={dateRangeEnd} onChange={handleDateRangeEndCommit} disabled={loading} />
                    <button
                      type="button"
                      onClick={handleApplyDateRange}
                      disabled={loading}
                      className="px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded-md hover:bg-primary-600 disabled:bg-gray-300 disabled:text-gray-600 disabled:cursor-not-allowed transition-colors"
                    >
                      {loading ? "Loading..." : "Apply"}
                    </button>
                  </>
                )}

                </div>
                {/* View mode toggle */}
                <div className="flex gap-0.5 bg-gray-200 rounded-md p-0.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleViewModeChange("granularity")}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      viewMode === "granularity" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Granularity
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewModeChange("daterange")}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                      viewMode === "daterange" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Date Range
                  </button>
                </div>
              </div>
              {viewMode === "daterange" && viewDateError && (
                <p className="mt-1 text-xs text-error-600">{viewDateError}</p>
              )}
            </div>
            {viewMode === "granularity" && (firstResponse?.period_label || displayRange.startDate) && (
              <p className="text-xs text-gray-500 -mt-1">
                {firstResponse?.period_label ||
                  `${formatGermanDateLabel(displayRange.startDate)} - ${formatGermanDateLabel(displayRange.endDate)}`}
              </p>
            )}

            {/* Unified legend row — always our own pill/badge-styled rendering (the
                built-in Chart.js legend is permanently hidden above) so the look stays
                consistent regardless of threshold/reference-line/comparison state:
                zone-pills layout when a threshold is configured (dedicated left block +
                absolutely-centered series badges); otherwise a single, simple centered
                row (plain chart, comparison-only, reference-line-only, or both) that
                still uses our own badge rendering so the periodRange tooltip works. */}
            {chartjsData && (
              <div className="relative flex items-center text-xs text-gray-500">
                {thresholdData ? (
                  <>
                    {/* Zone dots + threshold line legends — left */}
                    <div className="flex items-center gap-2 shrink-0">
                      {thresholdType !== "optimal_range" ? (
                        <>
                          <span className="inline-flex items-center gap-x-1.5 rounded bg-gray-100 px-2 py-0.5">
                            <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: ZONE_COLORS.good }} />
                            Good
                          </span>
                          <span className="inline-flex items-center gap-x-1.5 rounded bg-gray-100 px-2 py-0.5">
                            <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: ZONE_COLORS.warning }} />
                            Warning
                          </span>
                          <span className="inline-flex items-center gap-x-1.5 rounded bg-gray-100 px-2 py-0.5">
                            <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: ZONE_COLORS.bad }} />
                            Bad
                          </span>
                          <span className="text-gray-300">|</span>
                        </>
                      ) : (
                        <>
                          <span className="inline-flex items-center gap-x-1.5 rounded bg-gray-100 px-2 py-0.5">
                            <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: ZONE_COLORS.good }} />
                            In range
                          </span>
                          <span className="inline-flex items-center gap-x-1.5 rounded bg-gray-100 px-2 py-0.5">
                            <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: ZONE_COLORS.bad }} />
                            Out of range
                          </span>
                          <span className="text-gray-300">|</span>
                        </>
                      )}
                      {dashedBadges}
                      <span className="text-gray-300">|</span>
                    </div>
                    {/* Data series legends — absolutely centered over full row width */}
                    <div className="absolute inset-0 flex flex-wrap justify-center items-center gap-2 pointer-events-none">
                      {solidBadges}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-wrap justify-center items-center gap-2 w-full">
                    {dashedBadges}
                    {solidBadges}
                  </div>
                )}
              </div>
            )}

            {showChart && (
              <div className="flex-1 min-h-0 flex flex-col">
                <div ref={chartWrapperRef} className="flex-1 min-h-[180px] relative">
                  <div className="absolute inset-0">
                    <Line ref={chartRef} data={chartjsData} options={chartjsOptions} />
                  </div>
                </div>
              </div>
            )}


            {showNoData && (
              <TileStatusContent
                status="empty"
                title="No Data Available"
                message="No attribute data found for the selected configuration and time period."
                icon={
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                }
              />
            )}

            {showSelectionPrompt && (
              <TileStatusContent
                status="empty"
                title="Select Filters to Load Chart"
                message={
                  viewMode === "granularity"
                    ? "Granularity mode applies automatically: select a granularity and date."
                    : "Date Range mode requires clicking Apply after selecting dates."
                }
              />
            )}
          </>
        )}

        {error && (
          <TileStatusContent
            status="error"
            title="Failed to load chart"
            message={typeof error === "string" ? error : "Unexpected error."}
            action={
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (isMetadataLoaded && hasConfig) fetchChartData();
                    else fetchMetadata();
                  }}
                  className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600"
                >
                  Retry
                </button>
                <button
                  onClick={handleOpenSettings}
                  className="px-4 py-2 bg-gray-200 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-300"
                >
                  Open Settings
                </button>
              </div>
            }
          />
        )}
      </TileContent>

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={(v) => {
          if (!v) handleCloseSettings();
        }}
        getInitialValues={() => draftSettings || {}}
        onApply={handleApplySettings}
        title="Attribute Sum Line Chart Settings"
        isApplyDisabled={!canCalculate || loading}
        applyButtonText={loading ? "Calculating..." : "Apply and Calculate"}
      >
        <AttributeSumLineChartSettings
          title={settingsValues.title}
          attributeSource={settingsValues.attributeSource}
          objectTypes={objectTypes}
          eventTypes={eventTypes}
          objectTypeNumericAttributes={objectTypeNumericAttributes}
          eventTypeNumericAttributes={eventTypeNumericAttributes}
          localFilters={localFilters}
          updateLocalFilter={updateLocalFilter}
          referenceLine={settingsValues.referenceLine}
          onReferenceLineChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), referenceLine: v }))
          }
          mainLineColor={settingsValues.mainLineColor}
          onMainLineColorChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), mainLineColor: v }))
          }
          activeFile={activeFile}
          selectedObjectTypes={settingsValues.selectedObjectTypes}
          selectedObjectAttributes={settingsValues.selectedObjectAttributes}
          selectedEventTypes={settingsValues.selectedEventTypes}
          selectedEventAttributes={settingsValues.selectedEventAttributes}
          enabledGranularities={settingsValues.enabledGranularities}
          displayMode={settingsValues.displayMode}
          valueAggregation={settingsValues.valueAggregation}
          timestampAnchor={settingsValues.timestampAnchor}
          anchorActivity={settingsValues.anchorActivity}
          activityOptions={activityOptions}
          comparisonEnabled={settingsValues.comparisonEnabled}
          comparisonMode={settingsValues.comparisonMode}
          limitComparisonToCurrentProgress={settingsValues.limitComparisonToCurrentProgress}
          thresholdType={settingsValues.thresholdType}
          thresholdTarget={settingsValues.thresholdTarget}
          thresholdWarningPercentage={settingsValues.thresholdWarningPercentage}
          thresholdTargetPerDay={settingsValues.thresholdTargetPerDay}
          thresholdLowerThreshold={settingsValues.thresholdLowerThreshold}
          thresholdUpperThreshold={settingsValues.thresholdUpperThreshold}
          thresholdLowerThresholdPerDay={settingsValues.thresholdLowerThresholdPerDay}
          thresholdUpperThresholdPerDay={settingsValues.thresholdUpperThresholdPerDay}
          onTitleChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), title: v }))
          }
          onAttributeSourceChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), attributeSource: v }))
          }
          onSelectedObjectTypesChange={(v) => {
            setDraftSettings((p) => ({ ...(p || {}), selectedObjectTypes: v }));
            fetchNumericAttributesForObjectTypes(v);
          }}
          onSelectedObjectAttributesChange={(v) =>
            setDraftSettings((p) => ({
              ...(p || {}),
              selectedObjectAttributes: v,
            }))
          }
          onSelectedEventTypesChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), selectedEventTypes: v }))
          }
          onSelectedEventAttributesChange={(v) =>
            setDraftSettings((p) => ({
              ...(p || {}),
              selectedEventAttributes: v,
            }))
          }
          onEnabledGranularitiesChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), enabledGranularities: v }))
          }
          onDisplayModeChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), displayMode: v }))
          }
          onValueAggregationChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), valueAggregation: v }))
          }
          onTimestampAnchorChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), timestampAnchor: v }))
          }
          onAnchorActivityChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), anchorActivity: v }))
          }
          onComparisonEnabledChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), comparisonEnabled: v }))
          }
          onComparisonModeChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), comparisonMode: v }))
          }
          onLimitComparisonToCurrentProgressChange={(v) =>
            setDraftSettings((p) => ({
              ...(p || {}),
              limitComparisonToCurrentProgress: v,
            }))
          }
          onThresholdTypeChange={(v) =>
            setDraftSettings((p) => {
              const base = p || {};
              return {
                ...base,
                thresholdType: v,
                ...sanitizeThresholdFields(base, v),
              };
            })
          }
          onThresholdTargetChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), thresholdTarget: v }))
          }
          onThresholdWarningPercentageChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), thresholdWarningPercentage: v }))
          }
          onThresholdTargetPerDayChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), thresholdTargetPerDay: v }))
          }
          onThresholdLowerThresholdChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), thresholdLowerThreshold: v }))
          }
          onThresholdUpperThresholdChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), thresholdUpperThreshold: v }))
          }
          onThresholdLowerThresholdPerDayChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), thresholdLowerThresholdPerDay: v }))
          }
          onThresholdUpperThresholdPerDayChange={(v) =>
            setDraftSettings((p) => ({ ...(p || {}), thresholdUpperThresholdPerDay: v }))
          }
          dateRangeStart={dateRangeStart}
          dateRangeEnd={dateRangeEnd}
          onDateRangePresetChange={handleDateRangePresetChange}
          loading={loading}
        />
      </TileSettingsModal>
    </BaseTile>
  );
}

export default AttributeSumLineChart;
